import SEO from "../components/SEO";
import { Link } from "react-router-dom";
import {
  ArrowRightIcon,
  ArrowUpRightIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  CpuChipIcon,
  UsersIcon,
  BoltIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  BuildingOffice2Icon,
  ChatBubbleLeftRightIcon,
  RocketLaunchIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckSolid, StarIcon as StarSolid } from "@heroicons/react/24/solid";

/* ─── tiny data ─── */
const metrics = [
  { value: "500+", label: "Instituições activas" },
  { value: "40%",  label: "Redução de custos médios" },
  { value: "98%",  label: "Satisfação reportada" },
  { value: "24h",  label: "Activação garantida" },
];

const bentoFeatures = [
  {
    id: "central",
    Icon: CpuChipIcon,
    tag: "Core",
    title: "Gestão\nCentralizada",
    desc: "Todas as operações, processos e dados num único ecossistema. Chega ao fim dos sistemas desconexos.",
    size: "large",
    accent: "#2babe3",
  },
  {
    id: "auto",
    Icon: BoltIcon,
    tag: "Automação",
    title: "Zero tarefas manuais",
    desc: "Processos administrativos, financeiros e operacionais automatizados de ponta a ponta.",
    size: "small",
    accent: "#f59e0b",
  },
  {
    id: "analytics",
    Icon: ChartBarIcon,
    tag: "Analytics",
    title: "Dashboards em\ntempo real",
    desc: "Indicadores actualizados ao segundo. Decisões baseadas em dados, não em intuição.",
    size: "medium",
    accent: "#10b981",
  },
  {
    id: "security",
    Icon: ShieldCheckIcon,
    tag: "Segurança",
    title: "Controlo total\nde acessos",
    desc: "Permissões granulares por perfil, encriptação de dados e auditoria completa de cada acção.",
    size: "small",
    accent: "#ef4444",
  },
  {
    id: "people",
    Icon: UsersIcon,
    tag: "Pessoas",
    title: "Clientes, equipa &\ndepartamentos",
    desc: "Gestão unificada de todos os intervenientes da sua instituição com perfis detalhados.",
    size: "small",
    accent: "#8b5cf6",
  },
  {
    id: "comms",
    Icon: ChatBubbleLeftRightIcon,
    tag: "Comunicação",
    title: "Canal directo",
    desc: "Notificações automáticas, mensagens internas e histórico de comunicações com clientes.",
    size: "small",
    accent: "#06b6d4",
  },
  {
    id: "global",
    Icon: GlobeAltIcon,
    tag: "Mobilidade",
    title: "Qualquer\ndispositivo",
    desc: "100% online. Aceda a partir do escritório, casa ou em movimento.",
    size: "small",
    accent: "#2babe3",
  },
  {
    id: "docs",
    Icon: DocumentTextIcon,
    tag: "Integração",
    title: "Documentos &\nfluxos unificados",
    desc: "Serviços, contratos e workflows integrados. Uma plataforma, zero duplicações.",
    size: "small",
    accent: "#f97316",
  },
];

const beforeAfter = [
  { before: "Processos manuais e propensos a erro",           after: "Fluxos automatizados e auditáveis" },
  { before: "Dados dispersos em múltiplos sistemas",          after: "Informação centralizada e acessível" },
  { before: "Relatórios demorados e desactualizados",         after: "Dashboards em tempo real, sempre actuais" },
  { before: "Comunicação lenta e não documentada",            after: "Canal integrado com histórico completo" },
  { before: "Erros e retrabalho frequentes",                  after: "Precisão e rastreabilidade garantidas" },
];

const clientWins = [
  { n: "01", title: "Atendimento personalizado e imediato",        desc: "Os seus clientes recebem respostas instantâneas adaptadas ao seu perfil — sem filas, sem espera, sem frustração." },
  { n: "02", title: "Serviços e informações em tempo real",        desc: "Consulta de saldos, processos, documentos e histórico disponíveis a qualquer momento e em qualquer dispositivo." },
  { n: "03", title: "Acompanhamento online de solicitações",       desc: "Notificações automáticas em cada etapa do processo. O cliente nunca fica sem saber o estado da sua solicitação." },
  { n: "04", title: "Acesso seguro a documentos e comprovativos",  desc: "Recibos, contratos e relatórios disponíveis online com autenticação segura. Sem necessidade de deslocações." },
  { n: "05", title: "Transparência que gera confiança",            desc: "Clientes informados são clientes satisfeitos. Maior transparência traduz-se em maior fidelização e recomendação." },
];

const testimonials = [
  { name: "Dra. Maria José Ferreira", role: "Directora Administrativa · Colégio Os Brilhantes", text: "O que antes levava dias, hoje é feito em minutos. Os encarregados adoram poder acompanhar os pagamentos pelo telemóvel.", stars: 5 },
  { name: "Eng. António Lopes",       role: "Director Financeiro · Instituto IMETRO",            text: "Os dashboards mudaram a forma como tomamos decisões. Temos visibilidade total sem precisar de reuniões intermináveis.", stars: 5 },
  { name: "Dra. Conceição Teixeira",  role: "Reitora · Universidade Católica de Angola",         text: "A integração de todos os serviços reduziu significativamente os erros administrativos. A eficiência da nossa equipa triplicou.", stars: 5 },
];

export default function Instituicoes() {
  return (
    <>
      <SEO
        title="Gestão Integrada para Instituições em Angola"
        description="Plataforma completa de gestão institucional para escolas e universidades em Angola. Automatize processos, gerencie clientes e colaboradores, relatórios em tempo real. Activação em 24h."
        path="/instituicoes"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Plataforma para Instituições — PIA Angola",
          "url": "https://pia-payment.vercel.app/instituicoes",
          "description": "Solução completa de gestão para instituições de ensino em Angola.",
          "breadcrumb": { "@type": "BreadcrumbList", "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Início", "item": "https://pia-payment.vercel.app/" },
            { "@type": "ListItem", "position": 2, "name": "Instituições", "item": "https://pia-payment.vercel.app/instituicoes" }
          ]},
        }}
      />
      {/* ══════════════════════════════════════════
          HERO — editorial split
      ══════════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#020d18]">
        {/* Giant background number */}
        <span
          className="absolute right-0 top-1/2 -translate-y-1/2 text-[22vw] font-extrabold leading-none select-none pointer-events-none"
          style={{
            color: "transparent",
            WebkitTextStroke: "1px rgba(43,171,227,0.08)",
            letterSpacing: "-0.04em",
          }}
        >
          PIA
        </span>

        {/* Glow */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-125 h-125 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(43,171,227,0.12) 0%, transparent 70%)" }} />
          <div className="absolute bottom-0 right-1/3 w-72 h-72 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(5,105,163,0.2) 0%, transparent 70%)" }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-8 pt-32 pb-20 w-full">
          <div className="flex items-end justify-between gap-12 max-md:flex-col max-md:items-start">

            {/* Left — main headline */}
            <div className="flex-1 max-w-2xl">
              <div className="flex items-center gap-3 mb-10">
                <span className="w-8 h-px bg-blue-light" />
                <span className="text-blue-light text-xs font-semibold uppercase tracking-[0.2em]">Plataforma Institucional</span>
              </div>

              <h1
                className="font-extrabold leading-[1.0] mb-8 max-md:text-5xl"
                style={{ fontSize: "clamp(3rem, 6vw, 5.5rem)", letterSpacing: "-0.03em" }}
              >
                <span className="text-white">A plataforma que</span><br />
                <span style={{ color: "transparent", WebkitTextStroke: "1.5px rgba(255,255,255,0.25)" }}>
                  a sua instituição
                </span><br />
                <span className="text-transparent bg-clip-text"
                  style={{ backgroundImage: "linear-gradient(90deg, #2babe3 0%, #7dd3fc 60%, #2babe3 100%)" }}>
                  merece
                </span>
              </h1>

              <p className="text-white/45 text-lg leading-relaxed max-w-lg mb-12">
                Gestão centralizada, automação inteligente e dados em tempo real —
                tudo o que precisa para crescer de forma sustentável e escalável.
              </p>

              <div className="flex items-center gap-5 flex-wrap">
                <Link
                  to="/register"
                  className="group inline-flex items-center gap-3 text-[#020d18] font-bold px-7 py-4 rounded-full text-sm transition-all hover:scale-105"
                  style={{ background: "linear-gradient(90deg, #2babe3, #7dd3fc)" }}
                >
                  Registar Instituição
                  <span className="w-6 h-6 rounded-full bg-[#020d18]/15 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                    <ArrowRightIcon className="w-3 h-3" />
                  </span>
                </Link>
                <Link to="/contact"
                  className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm font-medium transition-colors">
                  Falar com a equipa <ArrowUpRightIcon className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>

            {/* Right — metric stack */}
            <div className="flex flex-col gap-3 min-w-64 max-md:flex-row max-md:flex-wrap">
              {metrics.map(({ value, label }) => (
                <div key={label}
                  className="border border-white/8 rounded-2xl px-6 py-5 hover:border-blue/30 hover:bg-white/3 transition-all group">
                  <p
                    className="font-extrabold leading-none mb-1 text-transparent bg-clip-text"
                    style={{ fontSize: "2.5rem", backgroundImage: "linear-gradient(135deg, #ffffff, #7dd3fc)" }}
                  >
                    {value}
                  </p>
                  <p className="text-white/40 text-xs font-medium">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
          style={{ background: "linear-gradient(to bottom, transparent, #f8fafc)" }} />
      </section>

      {/* ══════════════════════════════════════════
          BENTO GRID — recursos
      ══════════════════════════════════════════ */}
      <section className="py-24 bg-off-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-end justify-between mb-14 max-md:flex-col max-md:items-start max-md:gap-4">
            <div>
              <span className="text-blue-light text-xs font-semibold uppercase tracking-widest">O que a plataforma oferece</span>
              <h2 className="text-4xl font-extrabold text-dark mt-2 leading-tight max-md:text-2xl"
                style={{ letterSpacing: "-0.02em" }}>
                Tudo o que precisa,<br />num único lugar.
              </h2>
            </div>
            <p className="text-muted text-sm max-w-xs leading-relaxed text-right max-md:text-left">
              8 módulos integrados que eliminam a necessidade de múltiplos sistemas e aplicações desconexas.
            </p>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-4 grid-rows-3 gap-4 h-[680px] max-lg:h-auto max-lg:grid-cols-2 max-sm:grid-cols-1">

            {/* Card 1 — large (col 1-2, row 1-2) */}
            <div
              className="col-span-2 row-span-2 rounded-3xl p-8 flex flex-col justify-between relative overflow-hidden group hover:-translate-y-1 transition-all duration-500 max-lg:col-span-2 max-sm:col-span-1"
              style={{ background: "linear-gradient(135deg, #001627 0%, #002849 55%, #0569a3 100%)" }}
            >
              <div className="absolute -right-8 -top-8 w-48 h-48 rounded-full"
                style={{ background: "radial-gradient(circle, rgba(43,171,227,0.2) 0%, transparent 70%)" }} />
              <div className="absolute inset-0"
                style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "20px 20px" }} />

              <div className="relative">
                <span className="inline-block text-[10px] font-bold uppercase tracking-[0.15em] text-blue-light border border-blue-light/30 px-3 py-1 rounded-full mb-6">Core</span>
                <CpuChipIcon className="w-10 h-10 text-blue-light mb-5" />
              </div>
              <div className="relative">
                <h3 className="text-3xl font-extrabold text-white leading-tight mb-3" style={{ letterSpacing: "-0.02em" }}>
                  Gestão<br />Centralizada
                </h3>
                <p className="text-white/50 text-sm leading-relaxed max-w-sm">
                  Todas as operações, processos e dados institucionais num único ecossistema. Elimine silos e ganhe visibilidade total sobre a sua organização.
                </p>
              </div>
            </div>

            {/* Card 2 — automação (col 3, row 1) */}
            <div className="rounded-3xl p-6 bg-amber-50 border border-amber-100 flex flex-col gap-3 group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 max-lg:col-span-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Automação</span>
                <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <BoltIcon className="w-4 h-4 text-amber-600" />
                </div>
              </div>
              <p className="font-bold text-dark text-base leading-snug">Zero tarefas manuais</p>
              <p className="text-muted text-xs leading-relaxed flex-1">Processos administrativos, financeiros e operacionais automatizados de ponta a ponta.</p>
            </div>

            {/* Card 3 — segurança (col 4, row 1) */}
            <div className="rounded-3xl p-6 bg-red-50 border border-red-100 flex flex-col gap-3 group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 max-lg:col-span-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-red-500">Segurança</span>
                <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <ShieldCheckIcon className="w-4 h-4 text-red-500" />
                </div>
              </div>
              <p className="font-bold text-dark text-base leading-snug">Controlo total de acessos</p>
              <p className="text-muted text-xs leading-relaxed flex-1">Permissões granulares por perfil e departamento. Auditoria completa de cada acção.</p>
            </div>

            {/* Card 4 — analytics (col 3, row 2) */}
            <div className="rounded-3xl p-6 bg-emerald-50 border border-emerald-100 flex flex-col gap-3 group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 max-lg:col-span-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Analytics</span>
                <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <ChartBarIcon className="w-4 h-4 text-emerald-600" />
                </div>
              </div>
              <p className="font-bold text-dark text-base leading-snug">Dashboards em tempo real</p>
              <p className="text-muted text-xs leading-relaxed flex-1">KPIs actualizados ao segundo. Decisões baseadas em dados, não em intuição.</p>
            </div>

            {/* Card 5 — comunicação (col 4, row 2) */}
            <div className="rounded-3xl p-6 bg-sky-50 border border-sky-100 flex flex-col gap-3 group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 max-lg:col-span-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase tracking-widest text-sky-600">Comunicação</span>
                <div className="w-9 h-9 rounded-xl bg-sky-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <ChatBubbleLeftRightIcon className="w-4 h-4 text-sky-600" />
                </div>
              </div>
              <p className="font-bold text-dark text-base leading-snug">Canal directo integrado</p>
              <p className="text-muted text-xs leading-relaxed flex-1">Notificações automáticas e histórico de comunicações com colaboradores e clientes.</p>
            </div>

            {/* Row 3 — 4 small cards */}
            <div className="rounded-3xl p-6 bg-violet-50 border border-violet-100 flex items-center gap-4 group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 max-lg:col-span-1">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                <UsersIcon className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-violet-500 mb-0.5">Pessoas</p>
                <p className="font-bold text-dark text-sm leading-snug">Cadastro completo</p>
              </div>
            </div>

            <div className="rounded-3xl p-6 bg-teal-50 border border-teal-100 flex items-center gap-4 group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 max-lg:col-span-1">
              <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                <GlobeAltIcon className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-teal-600 mb-0.5">Mobilidade</p>
                <p className="font-bold text-dark text-sm leading-snug">Qualquer dispositivo</p>
              </div>
            </div>

            <div className="rounded-3xl p-6 bg-orange-50 border border-orange-100 flex items-center gap-4 group hover:-translate-y-1 hover:shadow-lg transition-all duration-300 max-lg:col-span-1">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                <DocumentTextIcon className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-orange-500 mb-0.5">Integração</p>
                <p className="font-bold text-dark text-sm leading-snug">Documentos unificados</p>
              </div>
            </div>

            <div
              className="rounded-3xl p-6 flex items-center gap-4 group hover:-translate-y-1 transition-all duration-300 max-lg:col-span-1"
              style={{ background: "linear-gradient(135deg, #2babe3, #016fb9)" }}
            >
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                <RocketLaunchIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/60 mb-0.5">Onboarding</p>
                <p className="font-bold text-white text-sm leading-snug">Activo em 24h</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          ANTES vs DEPOIS — editorial
      ══════════════════════════════════════════ */}
      <section className="py-24 bg-[#020d18] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="absolute right-0 top-0 w-96 h-96 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(43,171,227,0.1) 0%, transparent 70%)" }} />

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="mb-16">
            <span className="text-blue-light text-xs font-semibold uppercase tracking-widest">Transformação real</span>
            <h2 className="text-4xl font-extrabold text-white mt-3 leading-tight max-md:text-2xl"
              style={{ letterSpacing: "-0.02em" }}>
              O que muda na<br />
              <span className="text-transparent bg-clip-text"
                style={{ backgroundImage: "linear-gradient(90deg, #2babe3, #7dd3fc)" }}>
                sua instituição.
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-px max-md:grid-cols-1">
            {/* Before column header */}
            <div className="bg-white/3 border border-white/8 rounded-tl-2xl rounded-tr-2xl max-md:rounded-tr-none max-md:rounded-bl-none p-4 flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-red-400" />
              <span className="text-white/40 text-xs font-bold uppercase tracking-widest">Antes do PIA</span>
            </div>
            <div className="bg-white/3 border border-white/8 rounded-none p-4 flex items-center gap-3 max-md:rounded-none border-l-0 max-md:border-l max-md:border-t-0">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-emerald-400 text-xs font-bold uppercase tracking-widest">Com o PIA</span>
            </div>

            {beforeAfter.map(({ before, after }, i) => {
              const isLast = i === beforeAfter.length - 1;
              return (
                <>
                  <div key={`b-${i}`}
                    className={`bg-white/2 border-x border-b border-white/8 p-5 flex items-center gap-4 group
                      ${isLast ? "rounded-bl-2xl max-md:rounded-bl-none" : ""}`}>
                    <div className="w-6 h-6 rounded-full border border-red-400/30 flex items-center justify-center shrink-0">
                      <span className="w-2 h-0.5 bg-red-400/60 rounded" />
                    </div>
                    <p className="text-white/35 text-sm line-through decoration-red-400/30">{before}</p>
                  </div>
                  <div key={`a-${i}`}
                    className={`bg-emerald-950/20 border-x border-b border-white/8 border-l-0 max-md:border-l max-md:border-t-0 p-5 flex items-center gap-4 group
                      ${isLast ? "rounded-br-2xl max-md:rounded-br-2xl max-md:rounded-bl-2xl" : ""}`}>
                    <div className="w-6 h-6 rounded-full bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center shrink-0">
                      <CheckSolid className="w-3 h-3 text-emerald-400" />
                    </div>
                    <p className="text-white/75 text-sm font-medium">{after}</p>
                  </div>
                </>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          O QUE OFERECE AOS CLIENTES — alternating
      ══════════════════════════════════════════ */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="text-blue-light text-xs font-semibold uppercase tracking-widest">Valor entregue</span>
            <h2 className="text-4xl font-extrabold text-dark mt-3 leading-tight max-md:text-2xl"
              style={{ letterSpacing: "-0.02em" }}>
              O que os seus clientes<br />vão sentir.
            </h2>
            <p className="text-muted text-base mt-4 max-w-md mx-auto leading-relaxed">
              Quando a sua instituição opera com o PIA, os seus clientes percebem a diferença desde o primeiro contacto.
            </p>
          </div>

          <div className="flex flex-col">
            {clientWins.map(({ n, title, desc }, i) => (
              <div
                key={n}
                className={`flex items-center gap-10 py-10 border-b border-border last:border-0 max-md:flex-col max-md:gap-4 ${i % 2 === 1 ? "flex-row-reverse" : ""}`}
              >
                {/* Number */}
                <div className="shrink-0">
                  <span
                    className="text-7xl font-extrabold leading-none text-transparent bg-clip-text max-md:text-5xl"
                    style={{ backgroundImage: i % 2 === 0
                      ? "linear-gradient(135deg, #2babe3, rgba(43,171,227,0.2))"
                      : "linear-gradient(135deg, #016fb9, rgba(1,111,185,0.2))"
                    }}
                  >
                    {n}
                  </span>
                </div>

                {/* Divider line */}
                <div className="w-px h-16 bg-border shrink-0 max-md:hidden" />

                {/* Content */}
                <div className="flex-1">
                  <h3 className="text-xl font-extrabold text-dark mb-3 leading-tight max-md:text-base" style={{ letterSpacing: "-0.01em" }}>
                    {title}
                  </h3>
                  <p className="text-muted text-sm leading-relaxed max-w-lg">{desc}</p>
                </div>

                {/* Check */}
                <div className="shrink-0 max-md:hidden">
                  <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center"
                    style={{ background: "linear-gradient(135deg, #2babe3, #016fb9)" }}
                  >
                    <CheckSolid className="w-5 h-5 text-white" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          TESTEMUNHOS — magazine style
      ══════════════════════════════════════════ */}
      <section className="py-24 bg-off-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-end justify-between mb-14 max-md:flex-col max-md:gap-4">
            <div>
              <span className="text-blue-light text-xs font-semibold uppercase tracking-widest">Testemunhos</span>
              <h2 className="text-3xl font-extrabold text-dark mt-3" style={{ letterSpacing: "-0.02em" }}>
                Quem já transformou<br />a sua instituição.
              </h2>
            </div>
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => <StarSolid key={i} className="w-5 h-5 text-amber-400" />)}
              <span className="text-muted text-sm ml-2 self-center">4.9 / 5</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-6 max-md:grid-cols-1">
            {testimonials.map(({ name, role, text, stars }, i) => (
              <div
                key={name}
                className={`rounded-3xl p-8 flex flex-col gap-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                  i === 1
                    ? "text-white"
                    : "bg-white border border-border"
                }`}
                style={i === 1 ? { background: "linear-gradient(145deg, #001627, #002849 60%, #0569a3)" } : {}}
              >
                {/* Quote mark */}
                <span
                  className="text-6xl font-serif leading-none -mb-2"
                  style={{ color: i === 1 ? "rgba(43,171,227,0.4)" : "rgba(0,0,0,0.06)" }}
                >
                  "
                </span>

                <p className={`text-sm leading-relaxed flex-1 ${i === 1 ? "text-white/70" : "text-dark"}`}>
                  {text}
                </p>

                <div className="flex items-center gap-3 pt-4 border-t"
                  style={{ borderColor: i === 1 ? "rgba(255,255,255,0.1)" : undefined }}>
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-extrabold shrink-0"
                    style={{ background: i === 1 ? "rgba(43,171,227,0.3)" : "linear-gradient(135deg, #2babe3, #016fb9)" }}
                  >
                    {name[0]}
                  </div>
                  <div>
                    <p className={`font-bold text-sm ${i === 1 ? "text-white" : "text-dark"}`}>{name}</p>
                    <p className={`text-xs ${i === 1 ? "text-white/40" : "text-muted"}`}>{role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          CTA — full bleed com texto gigante
      ══════════════════════════════════════════ */}
      <section className="relative bg-[#020d18] py-32 overflow-hidden">
        {/* Giant background text */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
          <span
            className="text-[18vw] font-extrabold whitespace-nowrap"
            style={{
              color: "transparent",
              WebkitTextStroke: "1px rgba(43,171,227,0.06)",
              letterSpacing: "-0.04em",
            }}
          >
            COMECE HOJE
          </span>
        </div>

        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-150 h-64 rounded-full"
            style={{ background: "radial-gradient(ellipse, rgba(43,171,227,0.12) 0%, transparent 70%)" }} />
        </div>

        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <h2
            className="font-extrabold text-white leading-tight mb-6 max-md:text-3xl"
            style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", letterSpacing: "-0.03em" }}
          >
            Transforme a sua<br />
            <span className="text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(90deg, #2babe3, #7dd3fc)" }}>
              instituição.
            </span>{" "}
            Agora.
          </h2>

          <p className="text-white/40 text-base leading-relaxed mb-12 max-w-lg mx-auto">
            Junte-se a mais de 500 instituições angolanas. Activação gratuita, suporte dedicado e formação incluídos.
          </p>

          <div className="flex flex-wrap gap-4 justify-center mb-10">
            <Link
              to="/register"
              className="group inline-flex items-center gap-3 font-bold px-8 py-4 rounded-full text-sm transition-all hover:scale-105 text-[#020d18]"
              style={{ background: "linear-gradient(90deg, #2babe3, #7dd3fc)" }}
            >
              Registar Instituição Gratuitamente
              <span className="w-6 h-6 rounded-full bg-[#020d18]/15 flex items-center justify-center group-hover:translate-x-0.5 transition-transform">
                <ArrowRightIcon className="w-3 h-3" />
              </span>
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-white/30 text-white/60 hover:text-white font-medium px-8 py-4 rounded-full text-sm transition-all"
            >
              Falar com um consultor
            </Link>
          </div>

          {/* Guarantees */}
          <div className="flex flex-wrap gap-6 justify-center">
            {[
              "Activação em 24h",
              "Sem compromisso inicial",
              "Suporte dedicado",
              "Formação gratuita",
            ].map((item) => (
              <div key={item} className="flex items-center gap-2">
                <CheckSolid className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                <span className="text-white/40 text-xs">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
